// combatpro/treasure/treasureValidate.js
//
// Deterministic validation of a loaded Treasure catalogue against the contract.
// Dependency-free (no ajv) so it runs in the browser, in Node, and in tests. It
// enforces the machine-readable rules the JSON Schemas describe plus the cross-file
// invariants a schema alone cannot (globally-unique ids, vocab membership, level
// bounds, positive weights, profile category references). Returns { valid, errors }.
//
// No DOM, no optional chaining, no em dash.

function isStr(v) { return typeof v === 'string' && v.length > 0; }
function isNum(v) { return typeof v === 'number' && !isNaN(v); }

export function validateManifest(manifest) {
  var errors = [];
  if (!manifest || typeof manifest !== 'object') { return { valid: false, errors: ['manifest: not an object'] }; }
  if (!isStr(manifest.contractVersion)) { errors.push('manifest.contractVersion missing'); }
  if (!isStr(manifest.catalogueVersion)) { errors.push('manifest.catalogueVersion missing'); }
  if (!manifest.shards || !Array.isArray(manifest.shards.items)) { errors.push('manifest.shards.items must be an array'); }
  var v = manifest.vocab || {};
  ['categories', 'rarities', 'wealthTiers'].forEach(function (k) {
    if (!Array.isArray(v[k]) || v[k].length === 0) { errors.push('manifest.vocab.' + k + ' must be a non-empty array'); }
  });
  return { valid: errors.length === 0, errors: errors };
}

export function validateItems(items, vocab, seenIds) {
  var errors = [];
  var cats = {}; (vocab.categories || []).forEach(function (c) { cats[c] = true; });
  var rar = {}; (vocab.rarities || []).forEach(function (r) { rar[r] = true; });
  var tier = {}; (vocab.wealthTiers || []).forEach(function (t) { tier[t] = true; });
  seenIds = seenIds || {};
  if (!Array.isArray(items)) { return { valid: false, errors: ['items shard is not an array'] }; }
  for (var i = 0; i < items.length; i++) {
    var it = items[i];
    var where = 'item[' + i + ']' + (it && it.id ? ' (' + it.id + ')' : '');
    if (!it || typeof it !== 'object') { errors.push(where + ': not an object'); continue; }
    if (!isStr(it.id)) { errors.push(where + ': missing id'); }
    else if (seenIds[it.id]) { errors.push(where + ': duplicate id "' + it.id + '"'); }
    else { seenIds[it.id] = true; }
    if (!isStr(it.name)) { errors.push(where + ': missing name'); }
    if (!isStr(it.category)) { errors.push(where + ': missing category'); }
    else if (!cats[it.category]) { errors.push(where + ': category "' + it.category + '" not in vocab'); }
    if (!isStr(it.rarity)) { errors.push(where + ': missing rarity'); }
    else if (!rar[it.rarity]) { errors.push(where + ': rarity "' + it.rarity + '" not in vocab'); }
    if (!isStr(it.wealthTier)) { errors.push(where + ': missing wealthTier'); }
    else if (!tier[it.wealthTier]) { errors.push(where + ': wealthTier "' + it.wealthTier + '" not in vocab'); }
    if (!isNum(it.value) || it.value < 0) { errors.push(where + ': value must be a number >= 0'); }
    if (!isNum(it.weight) || it.weight <= 0) { errors.push(where + ': weight must be a number > 0'); }
    if (!isNum(it.minLevel) || !isNum(it.maxLevel)) { errors.push(where + ': minLevel/maxLevel required'); }
    else if (it.minLevel > it.maxLevel) { errors.push(where + ': minLevel > maxLevel'); }
  }
  return { valid: errors.length === 0, errors: errors };
}

export function validateProfiles(profiles, vocab) {
  var errors = [];
  var cats = {}; (vocab.categories || []).forEach(function (c) { cats[c] = true; });
  if (!Array.isArray(profiles)) { return { valid: false, errors: ['profiles shard is not an array'] }; }
  for (var i = 0; i < profiles.length; i++) {
    var p = profiles[i];
    var where = 'profile[' + i + ']' + (p && p.id ? ' (' + p.id + ')' : '');
    if (!p || !isStr(p.id)) { errors.push(where + ': missing id'); continue; }
    if (!isStr(p.kind)) { errors.push(where + ': missing kind'); }
    if (!p.categoryWeights || typeof p.categoryWeights !== 'object') { errors.push(where + ': categoryWeights required'); continue; }
    Object.keys(p.categoryWeights).forEach(function (c) {
      if (!cats[c]) { errors.push(where + ': categoryWeights references unknown category "' + c + '"'); }
      if (!isNum(p.categoryWeights[c]) || p.categoryWeights[c] < 0) { errors.push(where + ': weight for "' + c + '" must be >= 0'); }
    });
  }
  return { valid: errors.length === 0, errors: errors };
}

export function validateCurrency(currency) {
  var errors = [];
  if (!currency || !Array.isArray(currency.denominations) || currency.denominations.length === 0) {
    return { valid: false, errors: ['currency.denominations must be a non-empty array'] };
  }
  var ids = {};
  currency.denominations.forEach(function (d, i) {
    if (!isStr(d.id)) { errors.push('currency.denominations[' + i + '].id missing'); }
    else if (ids[d.id]) { errors.push('currency: duplicate denomination "' + d.id + '"'); }
    else { ids[d.id] = true; }
    if (!isNum(d.value) || d.value <= 0) { errors.push('currency.denominations[' + i + '].value must be > 0'); }
  });
  if (isStr(currency.base) && !ids[currency.base]) { errors.push('currency.base "' + currency.base + '" is not a denomination'); }
  return { valid: errors.length === 0, errors: errors };
}
